import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import {
  adminApi,
  type TermsAcceptanceReport,
  type TermsAcceptanceStatus,
  type TermsReportFilters
} from '../services/api';

const STATUSES: TermsAcceptanceStatus[] = ['current', 'previous', 'unknown', 'none'];
const PERSONAS = ['person', 'company', 'lawyer', 'inspector'];

const statusBadge: Record<TermsAcceptanceStatus, string> = {
  current: 'bg-soft-success text-success',
  previous: 'bg-soft-warning text-warning',
  unknown: 'bg-soft-info text-info',
  none: 'bg-soft-secondary text-secondary'
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

/**
 * Admin report: which terms version each private person and company user
 * has accepted. The basis for mailings when the terms change (ClickUp 869fcfjg9).
 */
export function AdminTermsAcceptanceReport() {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState<TermsReportFilters>({});
  const [report, setReport] = useState<TermsAcceptanceReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);

  const locale = i18n.language === 'en' ? 'en-GB' : 'sv-SE';
  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(value));

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setFailed(false);
    adminApi.getTermsAcceptanceReport(filters)
      .then((data) => {
        if (!cancelled) setReport(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters]);

  const statusCounts = useMemo(() => {
    const counts: Record<TermsAcceptanceStatus, number> = { current: 0, previous: 0, unknown: 0, none: 0 };
    report?.rows.forEach((row) => {
      counts[row.status] += 1;
    });
    return counts;
  }, [report]);

  // Newest version first.
  const versions = useMemo(() => [...(report?.versions ?? [])].reverse(), [report]);

  const setFilter = (key: keyof TermsReportFilters, value: string) =>
    setFilters((previous) => ({ ...previous, [key]: value }));

  const handleExport = async () => {
    setIsExporting(true);
    setExportFailed(false);
    try {
      const { blob, filename } = await adminApi.exportTermsAcceptanceReport(filters);
      downloadBlob(blob, filename);
    } catch {
      setExportFailed(true);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col-12">
            <Breadcrumb
              items={[
                { label: t('termsVersions.report.breadcrumb.home'), path: '/admin/home' },
                { label: t('termsVersions.report.breadcrumb.reports') },
                { label: t('termsVersions.report.breadcrumb.current') }
              ]}
            />
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div>
                <h4 className="fw-bold mb-1">{t('termsVersions.report.title')}</h4>
                <p className="text-muted mb-0">{t('termsVersions.report.subtitle')}</p>
              </div>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleExport}
                disabled={isExporting}
              >
                <i className="feather-download me-1" aria-hidden="true"></i>
                {isExporting ? t('termsVersions.report.exporting') : t('termsVersions.report.exportCsv')}
              </button>
            </div>
            {exportFailed && (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {t('termsVersions.report.exportError')}
              </div>
            )}
          </div>
        </div>

        <Card className="mb-4" title={t('termsVersions.report.versionsTitle')} icon="layers">
          {versions.length === 0 ? (
            <p className="text-muted mb-0">
              {isLoading ? t('common.loading') : t('termsVersions.report.noVersions')}
            </p>
          ) : (
            <ul className="list-unstyled mb-0 terms-acceptances__list">
              {versions.map((version) => {
                const isCurrent = report?.currentVersion?.id === version.id;
                return (
                  <li key={version.id} className="terms-acceptances__item">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Link
                        to={`/anvandarvillkor?version=${encodeURIComponent(version.version)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fw-semibold text-break"
                      >
                        {version.title} · {t('termsVersions.versionLabel', { version: version.version })}
                      </Link>
                      {isCurrent && (
                        <span className="badge bg-soft-success text-success">{t('termsVersions.history.current')}</span>
                      )}
                      <span className="badge bg-soft-primary text-primary">{t(`termsVersions.track.${version.termsTrack}`)}</span>
                      {version.changeType && (
                        <span className="badge bg-soft-secondary text-secondary">
                          {t(`termsVersions.changeType.${version.changeType}`)}
                        </span>
                      )}
                    </div>
                    <div className="small text-muted mt-1">
                      {t('termsVersions.report.publishedAt', { date: formatDate(version.publishedAt) })}
                      {' · '}
                      {t('termsVersions.report.latestAcceptedCount', { count: version.latestAcceptedCount })}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card className="mb-4">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-sm-6 col-lg-3">
              <label className="form-label" htmlFor="termsReportStatus">{t('termsVersions.report.filters.status')}</label>
              <select
                id="termsReportStatus"
                className="form-select"
                value={filters.status ?? ''}
                onChange={(event) => setFilter('status', event.target.value)}
              >
                <option value="">{t('termsVersions.report.filters.all')}</option>
                {STATUSES.map((status) => (
                  <option key={status} value={status}>{t(`termsVersions.status.${status}`)}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-6 col-lg-3">
              <label className="form-label" htmlFor="termsReportPersona">{t('termsVersions.report.filters.persona')}</label>
              <select
                id="termsReportPersona"
                className="form-select"
                value={filters.personaType ?? ''}
                onChange={(event) => setFilter('personaType', event.target.value)}
              >
                <option value="">{t('termsVersions.report.filters.all')}</option>
                {PERSONAS.map((persona) => (
                  <option key={persona} value={persona}>{t(`termsVersions.persona.${persona}`)}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-6 col-lg-3">
              <label className="form-label" htmlFor="termsReportTrack">{t('termsVersions.report.filters.track')}</label>
              <select
                id="termsReportTrack"
                className="form-select"
                value={filters.termsTrack ?? ''}
                onChange={(event) => setFilter('termsTrack', event.target.value)}
              >
                <option value="">{t('termsVersions.report.filters.all')}</option>
                <option value="pilot">{t('termsVersions.track.pilot')}</option>
                <option value="commercial">{t('termsVersions.track.commercial')}</option>
              </select>
            </div>
            <div className="col-12 col-sm-6 col-lg-3">
              <label className="form-label" htmlFor="termsReportVersion">{t('termsVersions.report.filters.version')}</label>
              <select
                id="termsReportVersion"
                className="form-select"
                value={filters.version ?? ''}
                onChange={(event) => setFilter('version', event.target.value)}
              >
                <option value="">{t('termsVersions.report.filters.all')}</option>
                {versions.map((version) => (
                  <option key={version.id} value={version.version}>
                    {t('termsVersions.versionLabel', { version: version.version })}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        <Card>
          {failed ? (
            <div className="alert alert-danger mb-0" role="alert">{t('termsVersions.report.loadError')}</div>
          ) : isLoading && !report ? (
            <div className="d-flex align-items-center gap-2 text-muted" role="status">
              <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
              {t('common.loading')}
            </div>
          ) : (
            <>
              <div className="d-flex flex-wrap gap-2 mb-3" data-testid="terms-report-counts">
                {STATUSES.map((status) => (
                  <span key={status} className={`badge ${statusBadge[status]}`}>
                    {t(`termsVersions.status.${status}`)}: {statusCounts[status]}
                  </span>
                ))}
              </div>
              {report && report.rows.length === 0 ? (
                <p className="text-center text-muted py-4 mb-0">{t('termsVersions.report.empty')}</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th>{t('termsVersions.report.columns.user')}</th>
                        <th>{t('termsVersions.report.columns.persona')}</th>
                        <th>{t('termsVersions.report.columns.status')}</th>
                        <th>{t('termsVersions.report.columns.version')}</th>
                        <th>{t('termsVersions.report.columns.acceptedAt')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report?.rows.map((row) => (
                        <tr key={row.userId}>
                          <td>
                            <div className="fw-semibold text-break">{row.fullName}</div>
                            <div className="small text-muted text-break">{row.email}</div>
                            {row.companyName && <div className="small text-muted text-break">{row.companyName}</div>}
                          </td>
                          <td>{t(`termsVersions.persona.${row.personaType}`, row.personaType)}</td>
                          <td>
                            <span className={`badge ${statusBadge[row.status]}`}>{t(`termsVersions.status.${row.status}`)}</span>
                          </td>
                          <td>
                            {row.latest ? (
                              <>
                                <div className="text-nowrap">{t('termsVersions.versionLabel', { version: row.latest.version })}</div>
                                <div className="small text-muted">{t(`termsVersions.track.${row.latest.termsTrack}`)}</div>
                              </>
                            ) : (
                              <span className="text-muted">{t('termsVersions.report.noValue')}</span>
                            )}
                          </td>
                          <td>
                            {row.latest ? (
                              <>
                                <div className="text-nowrap">{formatDate(row.latest.acceptedAt)}</div>
                                <div className="small text-muted">{t(`termsVersions.context.${row.latest.context}`)}</div>
                              </>
                            ) : (
                              <span className="text-muted">{t('termsVersions.report.noValue')}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
